import { Component, h, Listen, Prop, State } from '@stencil/core';
import {
  addError,
  checkCurrentStepValidity,
  closeCheckout,
  closeProcessingSpinner,
  fetchCustomer,
  goToNextStep,
  goToStep,
  initSteps,
  markComponentAsLoaded,
  openProcessingSpinner,
  removeError,
  resetBooking,
  saveDataFormInputValues,
  savePersonalDetails,
  storeBillingValues,
} from '../../../redux/actions';
import { store } from '../../../redux/store';
import { App, BookingDetails, OrderDetails, RtrError, Shipping, ShippingPriceType, StepItem, StepsError, System } from '../../../redux/types';
import { Unsubscribe } from '@stencil/redux';
import { trans } from '../../../utils/trans';
import { StepErrorType } from '../../../types/types';
import { Steps } from './types';
import { getSteps, RootState } from '../../../redux/reducers';

@Component({
  tag: 'rentware-checkout-modal',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareCheckoutModal {
  @Prop({ mutable: true }) orderDetailsState: OrderDetails;
  @Prop({ mutable: true }) currentStepState: StepItem;
  @Prop({ mutable: true }) shippingState: Shipping;
  @Prop({ mutable: true }) stepsState: StepItem[];
  @Prop({ mutable: true }) customerState;
  @Prop({ mutable: true }) app: App;
  @Prop({ mutable: true }) showCheckoutState: boolean;
  @Prop({ mutable: true }) bookingCompleted: boolean;
  @Prop({ mutable: true }) bookingExpired: boolean;
  @Prop({ mutable: true }) system: System;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) loadedSteps;
  @Prop({ mutable: true }) billingStepValues: any;
  @Prop({ mutable: true }) bookingDetails: BookingDetails;
  @Prop({ mutable: true }) errors: RtrError[];

  @State() dataFormStepValues: any;
  @State() areBreadCrumbsOpen: boolean = false;
  @State() isBreadCrumbsAnimationOpen = false;
  @State() stepErrors: StepsError[] = [];
  @State() isCheckoutWaiting: boolean = false;
  @State() isSavingDataForm: boolean = false;

  goToNextStep!: typeof goToNextStep;
  goToStep!: typeof goToStep;
  savePersonalDetails!: typeof savePersonalDetails;
  saveDataFormInputValues!: typeof saveDataFormInputValues;
  resetBooking!: typeof resetBooking;
  fetchCustomer!: typeof fetchCustomer;
  checkCurrentStepValidity!: typeof checkCurrentStepValidity;
  closeCheckout!: typeof closeCheckout;
  openProcessingSpinner!: typeof openProcessingSpinner;
  closeProcessingSpinner!: typeof closeProcessingSpinner;
  markComponentAsLoaded!: typeof markComponentAsLoaded;
  storeBillingValues!: typeof storeBillingValues;
  initSteps!: typeof initSteps;
  unsubscribe!: Unsubscribe;
  billingStepRef: any;
  shippingStepRef: any;
  dataFormCustomStepRef: any;
  addError!: typeof addError;
  removeError!: typeof removeError;

  @Listen('beforeunload', { target: 'window' })
  onBeforeUnload() {
    return 'Are you sure you want to leave?'; //it is not supported anymore
  }

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        currentStepState: state.app.currentStep,
        app: state.app,
        shippingState: state.shipping,
        orderDetailsState: state.bookingDetails.orderDetails,
        stepsState: getSteps(state),
        showCheckoutState: state.app.showCheckout,
        bookingCompleted: state.app.bookingCompleted,
        bookingExpired: state.app.bookingExpired,
        system: state.system,
        locale: state.app.locale,
        customerState: state.app.customer,
        loadedSteps: state.app.loadedSteps,
        billingStepValues: state.app.billingStepValues,
        bookingDetails: state.bookingDetails,
        errors: state.errors,
      };
    });

    store.mapDispatchToProps(this, {
      goToNextStep,
      goToStep,
      savePersonalDetails,
      saveDataFormInputValues,
      resetBooking,
      fetchCustomer,
      checkCurrentStepValidity,
      closeCheckout,
      markComponentAsLoaded,
      openProcessingSpinner,
      closeProcessingSpinner,
      storeBillingValues,
      initSteps,
      addError,
      removeError,
    });

    // Stencil listener looks to be bugged
    window.onkeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        this.handleCloseCheckout();
      }
    };

    this.startWaiting();
    this.initSteps(this.finishWaiting);
    this.markComponentAsLoaded(RentwareCheckoutModal.name);
  }

  componentWillRender() {
    if (this.currentStepState?.kind === 'payment' && !this.customerState) {
      // customer data required for some payment methods
      this.fetchCustomer();
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  get customerEmail() {
    return this.billingStepValues?.email || this.app.customer?.customer?.email;
  }

  handleStepValidity = (onValid: any, onNotValid: any = () => {}) => {
    this.checkCurrentStepValidity((valid, reason) => {
      if (valid) {
        this.removeStepError(this.currentStepState.id, StepErrorType.ValidationStepMinQuantityError);
        this.removeStepError(this.currentStepState.id, StepErrorType.ValidationStepMaxQuantityError);
        this.removeStepError(this.currentStepState.id, StepErrorType.StepInvalid);

        onValid();
      } else {
        if (reason.id === 'min') {
          this.addStepError(
            this.currentStepState.id,
            StepErrorType.ValidationStepMinQuantityError,
            trans('checkout.validationStepMinQuantityError', this.locale, this.system.translations, { quantity: reason.context.min }),
          );
          onNotValid();
        } else if (reason.id === 'max') {
          this.addStepError(
            this.currentStepState.id,
            StepErrorType.ValidationStepMaxQuantityError,
            trans('checkout.validationStepMaxQuantityError', this.locale, this.system.translations, { quantity: reason.context.max }),
          );
          onNotValid();
        } else {
          this.addStepError(this.currentStepState.id, StepErrorType.StepInvalid);
          onNotValid();
        }
      }
    }, onNotValid);
  };

  addStepError = (id, type: StepErrorType, message: string = null) => {
    if (!this.stepErrors.find(error => error.stepId === id) || !this.stepErrors.find(error => error.type === type)) {
      this.stepErrors = [...this.stepErrors, { stepId: id, type: type, message: message }];
    }
  };

  removeStepError = (id, type: StepErrorType) => {
    if (!type) {
      this.stepErrors = this.stepErrors.filter(error => error.stepId !== id);
    } else {
      this.stepErrors = this.stepErrors.filter(error => error.stepId !== id && error.type !== type);
    }
  };

  handleGoToFirstErrorClick = () => {
    if (this.currentStepState.kind === 'shipping') {
      this.shippingStepRef.goToFirstError();
    }
    if (this.currentStepState.kind === 'personal-details') {
      this.billingStepRef.goToFirstError();
    }
    if (this.currentStepState.kind === Steps.Custom && this.currentStepState.config.dataFormId) {
      this.dataFormCustomStepRef.goToFirstError();
    }
  };

  validateBilling = async (scroll: boolean = true) => {
    return await this.billingStepRef.validate(
      () => {
        this.removeStepError(this.currentStepState.id, StepErrorType.BillingNotAllFieldsValid);
        this.finishWaiting();
      },
      () => {
        this.addStepError(this.currentStepState.id, StepErrorType.BillingNotAllFieldsValid);
        if (scroll) {
          this.handleGoToFirstErrorClick();
        }
        this.finishWaiting();
      },
    );
  };

  validateShipping = async (scroll: boolean = true) => {
    const selectedShipping = this.shippingState?.options?.find(option => option.id === this.shippingState?.selectedMethodId);
    if (selectedShipping) {
      this.removeStepError(this.currentStepState.id, StepErrorType.ShippingNoOptionSelected);

      if (selectedShipping.priceType === ShippingPriceType.Distance && selectedShipping.distance === 0) {
        this.addStepError(this.currentStepState.id, StepErrorType.ShippingDistanceProblem);
        if (scroll) {
          this.handleGoToFirstErrorClick();
        }

        this.finishWaiting();
        return false;
      } else {
        this.removeStepError(this.currentStepState.id, StepErrorType.ShippingDistanceProblem);

        this.finishWaiting();
        return true;
      }
    } else {
      this.addStepError(this.currentStepState.id, StepErrorType.ShippingNoOptionSelected);
      if (scroll) {
        this.handleGoToFirstErrorClick();
      }

      this.finishWaiting();
      return false;
    }
  };

  validateDataForm = async (scroll: boolean = true) => {
    return await this.dataFormCustomStepRef.validate(
      () => {
        this.removeStepError(this.currentStepState.id, StepErrorType.FormNotAllFieldsValid);
        this.finishWaiting();
      },
      () => {
        this.addStepError(this.currentStepState.id, StepErrorType.FormNotAllFieldsValid);
        if (scroll) {
          this.handleGoToFirstErrorClick();
        }
        this.finishWaiting();
      },
    );
  };

  handleValidate = async (scroll: boolean = true) => {
    switch (this.currentStepState.kind) {
      case 'personal-details':
        return await this.validateBilling(scroll);
      case 'shipping':
        return await this.validateShipping(scroll);
      case Steps.Custom:
        if (this.currentStepState.config?.dataFormId) {
          return await this.validateDataForm(scroll);
        }
    }

    return true;
  };

  startWaiting = () => {
    this.isCheckoutWaiting = true;
  };

  finishWaiting = () => {
    this.isCheckoutWaiting = false;
  };

  handleCheckoutWaiting = (value: boolean) => {
    this.isCheckoutWaiting = value;
  };

  startPayment = () => {
    this.startWaiting();
    this.openProcessingSpinner();
  };

  finishPayment = () => {
    this.finishWaiting();
    this.closeProcessingSpinner();
  };

  finishWaitingAndGotoTheNextStep = () => {
    this.initSteps(() => {
      this.goToNextStep();
      this.finishWaiting();
    }, this.finishWaiting);
  };

  finishPaymentAndGotoTheNextStep = () => {
    this.finishWaitingAndGotoTheNextStep();
    this.closeProcessingSpinner();
  };

  handleNextBtnClick = async () => {
    if (this.currentStepState.kind === 'payment') {
      this.finishPaymentAndGotoTheNextStep();
    } else {
      this.startWaiting();
      if (await this.handleValidate()) {
        if (this.currentStepState.kind === 'personal-details') {
          await this.saveBillingValues(this.finishWaitingAndGotoTheNextStep, this.finishWaiting);
        } else if (this.currentStepState.kind === Steps.Custom && this.currentStepState.config?.dataFormId) {
          await this.saveDataFormValues(true, this.finishWaitingAndGotoTheNextStep, this.finishWaiting);
        } else {
          this.handleStepValidity(this.finishWaitingAndGotoTheNextStep, this.finishWaiting);
        }
      } else {
        this.handleGoToFirstErrorClick();
        this.finishWaiting();
      }
    }
  };

  saveBillingValues = async (onSuccess, onError = () => {}) => {
    this.billingStepRef.saveData(values => {
      this.storeBillingValues(values);

      this.savePersonalDetails(
        this.orderDetailsState.id,
        values,
        false,
        () => {
          if (!values.shippingAddressSameAsAddress) {
            this.savePersonalDetails(
              this.orderDetailsState.id,
              values,
              true,
              () => {
                onSuccess();
                this.fetchCustomer();
              },
              null,
            );
          } else {
            onSuccess();
            this.fetchCustomer();
          }
        },
        onError,
      );
    });
  };

  saveDataFormValues = async (sendFiles: boolean = true, onSuccess, onError = () => {}) => {
    // On single input change being saved, we don't want to communicate/animate (it happens frequently and is quick).
    // Only final form save (files are sent then too) set isSavingDataForm to true.
    this.isSavingDataForm = sendFiles;

    this.dataFormCustomStepRef.saveData((values) => {
      const cleanedValues = new FormData();
      for (const [key, value] of values.entries()) {
        if (value === undefined || value === 'undefined') {
          cleanedValues.append(key, '');
        }

        const isBlob = value instanceof Blob
        if (!isBlob || sendFiles) {
          cleanedValues.append(key, value);
        }
      }

      this.dataFormStepValues = cleanedValues;

      this.saveDataFormInputValues(
        this.currentStepState.config.dataFormId,
        cleanedValues,
        () => {
          this.isSavingDataForm = false;
          onSuccess?.();
        },
        () => {
          this.isSavingDataForm = false;
          onError();
        }
      );
    });
  };

  handleBreadCrumbsLinkClick = id => {
    this.handleCloseBreadCrumbs(() => this.handleGotoStep(id));
  };

  handleGotoStep = (id: string): void => {
    if (this.currentStepState.kind === 'personal-details') {
      this.storeBilling(() => this.goToStep(id));
    } else {
      this.goToStep(id);
    }
  };

  storeBilling = onSuccess => {
    this.billingStepRef.saveDataWithoutValidation(values => {
      this.storeBillingValues(values);
    });

    onSuccess?.();
  };

  handleOpenBreadCrumbs = () => {
    this.areBreadCrumbsOpen = true;
    setTimeout(() => {
      this.isBreadCrumbsAnimationOpen = true;
    }, 200);
  };

  handleCloseBreadCrumbs = (callback = null) => {
    this.isBreadCrumbsAnimationOpen = false;
    setTimeout(() => {
      this.areBreadCrumbsOpen = false;
      if (typeof callback === 'function') {
        callback?.();
      }
    }, 200);
  };

  handleCloseCheckout = () => {
    this.closeCheckout();
    if (this.bookingCompleted) {
      this.resetBooking();
    }
  };

  get stepFinishedLoading() {
    return !!this.loadedSteps[this.currentStepState?.id];
  }

  get buttonNextStepText(): string {
    return trans('cart.buttonNextStep', this.locale, this.system.translations);
  }

  get isStepWaiting(): boolean {
    return !this.stepFinishedLoading || this.isCheckoutWaiting;
  }

  get visitedSteps(): string[] {
    const stepsList = this.stepsState.map(step => step.id);
    const currentStepId = this.currentStepState?.id;
    const currentStepIndex = stepsList.findIndex(stepId => stepId === currentStepId);
    return stepsList.slice(0, currentStepIndex);
  }

  render() {
    if (this.bookingExpired) {
      return null;
    }

    if (!this.showCheckoutState || !this.orderDetailsState || !this.bookingDetails || !this.orderDetailsState.id) {
      return null;
    }

    return (
      <rentware-checkout-template
        onComponentLoaded={name => this.markComponentAsLoaded(name)}
        currentStep={this.currentStepState}
        steps={this.stepsState}
        visitedSteps={this.visitedSteps}
        handleGoToFirstErrorClick={this.handleGoToFirstErrorClick}
        buttonNextStepText={this.buttonNextStepText}
        isWaiting={this.isStepWaiting}
        isSavingDataForm={this.isSavingDataForm}
        bookingDetails={this.bookingDetails}
        areBreadCrumbsOpen={this.areBreadCrumbsOpen}
        isAnimationOpen={this.isBreadCrumbsAnimationOpen}
        onBreadCrumbsLinkClick={this.handleBreadCrumbsLinkClick}
        onStepClick={this.handleOpenBreadCrumbs}
        onCloseBreadCrumbsClick={this.handleCloseBreadCrumbs}
        onCloseCheckout={this.handleCloseCheckout}
        refBilling={el => (this.billingStepRef = el)}
        billingStepValues={this.billingStepValues}
        onGoToStep={this.handleGotoStep}
        onFormValid={() => this.removeStepError(this.currentStepState.id, StepErrorType.BillingNotAllFieldsValid)}
        refShipping={el => (this.shippingStepRef = el)}
        refDataForm={el => (this.dataFormCustomStepRef = el)}
        bookingCompleted={this.bookingCompleted}
        orderReference={this.orderDetailsState.id}
        markPaymentAsCompleted={this.closeProcessingSpinner}
        onNextBtnClick={this.handleNextBtnClick}
        stepErrors={this.stepErrors}
        errors={this.errors}
        removeError={this.removeStepError}
        onValidateClick={this.handleValidate}
        setCheckoutWaiting={this.handleCheckoutWaiting}
        saveDataFormValues={this.saveDataFormValues}
      />
    );
  }
}
